import { useQuery } from '@tanstack/react-query';
import { searchProducts, type ProductSearchResponse } from '../lib/api';

interface UseProductSearchOptions {
  /** Minimum query length before searching (default: 2) */
  minLength?: number;
  /** Maximum results to return (default: 10) */
  limit?: number;
  /** Filter by category ID */
  categoryId?: string;
  /** Debounce delay in ms (handled externally) */
  enabled?: boolean;
}

/**
 * Hook for searching products with autocomplete
 *
 * Uses React Query with a short staleTime for responsive search.
 * Disabled when query is too short.
 */
export function useProductSearch(
  query: string,
  options: UseProductSearchOptions = {}
) {
  const {
    minLength = 2,
    limit = 10,
    categoryId,
    enabled = true,
  } = options;

  const trimmedQuery = query.trim();
  const shouldSearch = enabled && trimmedQuery.length >= minLength;

  const {
    data,
    isLoading,
    isError,
    error,
    isFetching,
  } = useQuery<ProductSearchResponse, Error>({
    queryKey: ['product-search', trimmedQuery, categoryId, limit],
    queryFn: () => searchProducts(trimmedQuery, { limit, categoryId }),
    enabled: shouldSearch,
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes (formerly cacheTime)
  });

  return {
    products: data?.products ?? [],
    total: data?.total ?? 0,
    isLoading: shouldSearch && isLoading,
    isFetching,
    isError,
    error,
    hasResults: (data?.products.length ?? 0) > 0,
    isEmpty: shouldSearch && !isLoading && (data?.products.length ?? 0) === 0,
  };
}
